import { useQuery } from "@tanstack/react-query"
import { client, APIError } from "@/api"

export type EmailPreviewTemplateId =
  | "team-invitation"
  | "team-added"
  | "invitation-accepted"
  | "invitation-declined"
  | "registration"
  | "email-verification"
  | "password-reset"
  | "game-exchange"
  | "export-complete"
  | "export-failed"
  | "welcome-offer-outreach"
  | "welcome-offer-details-request"
  | "welcome-offer-team-ready"

export type EmailPreviewUrlMode = "current" | "development" | "production"

export interface EmailPreviewParams {
  urlMode: EmailPreviewUrlMode
  teamName: string
  inviterName: string
  inviterEmail: string
  inviteeName: string
  inviteeEmail: string
  senderName: string
  sourceTeamName: string
  gameTitle: string
  exchangeMessage: string
  totalClips: number
  exportError: string
  coachFirstName: string
  /** ISO date string ("YYYY-MM-DD") — welcome-offer-team-ready's comp end date. */
  compEndDate: string
}

export interface EmailPreviewTemplate {
  id: EmailPreviewTemplateId
  name: string
  audience: string
  description: string
  /** Preview fixture only; must not be sent yet. The page hides copy controls. */
  onHold?: true
}

export interface EmailPreviewMessage {
  to_email: string
  from_email: string
  subject: string
  message: string
  message_html?: string
  preheader?: string
}

export interface EmailPreviewResponse {
  template: EmailPreviewTemplate
  urlMode: EmailPreviewUrlMode
  frontendUrl: string
  preheader: string | null
  email: EmailPreviewMessage
  previewHtml: string
}

interface EmailPreviewTemplatesResponse {
  templates: EmailPreviewTemplate[]
  defaults: EmailPreviewParams
}

export const defaultEmailPreviewParams: EmailPreviewParams = {
  urlMode: "current",
  teamName: "Easton Tigers Varsity",
  inviterName: "Coach Tait Larson",
  inviterEmail: "coach@playside.co",
  inviteeName: "Jeremy Hunt",
  inviteeEmail: "jeremy.hunt@example.com",
  senderName: "Coach Riley Moore",
  sourceTeamName: "Northview Falcons",
  gameTitle: "Week 6 vs Ridgefield",
  exchangeMessage: "Here is the film we discussed after Friday's game.",
  totalClips: 42,
  exportError: "The export service lost connection while packaging clips.",
  coachFirstName: "Jeremy",
  compEndDate: "2027-09-16",
}

export function useEmailPreviewTemplates() {
  return useQuery<EmailPreviewTemplatesResponse>({
    queryKey: ["admin", "email-previews", "templates"],
    queryFn: async () => {
      const response = await (client.admin as any)["email-previews"].$get()
      if (!response.ok) {
        const error = (await response.json()) as any
        throw new APIError(
          response.status,
          error.code || "FETCH_EMAIL_PREVIEW_TEMPLATES_FAILED",
          new Error(error.message || "Failed to fetch email preview templates")
        )
      }
      return (await response.json()) as EmailPreviewTemplatesResponse
    },
  })
}

export function useEmailPreview(
  templateId: EmailPreviewTemplateId,
  params: EmailPreviewParams,
  enabled: boolean
) {
  return useQuery<EmailPreviewResponse>({
    queryKey: ["admin", "email-previews", templateId, params],
    enabled,
    queryFn: async () => {
      const response = await (client.admin as any)["email-previews"][":templateId"].$get({
        param: { templateId },
        query: params,
      })
      if (!response.ok) {
        const error = (await response.json()) as any
        throw new APIError(
          response.status,
          error.code || "FETCH_EMAIL_PREVIEW_FAILED",
          new Error(error.message || "Failed to fetch email preview")
        )
      }
      return (await response.json()) as EmailPreviewResponse
    },
  })
}
